import React, {useState, useMemo} from 'react';
import {Container, Grid, Stack, Title, Text, Group, Button, Badge, List, ThemeIcon, Loader, Center, Table} from '@mantine/core';
import {
  IconStar,
  IconUserPlus,
  IconBell,
  IconFolder,
  IconFolderShare,
  IconActivity,
  IconChevronRight,
  IconFile,
  IconMusic,
} from '@tabler/icons-react';
import {useNavigate} from 'react-router-dom';
import {isAudioFile} from '../../utils/file_utils';
import {PlayIcon} from '../../modules/audio_player/audio_player_module';
import {navigateToPathForReason} from '../../modules/navigation/navigation_module';
import {DashboardCard} from './components/DashboardCard';
import {ProjectTable} from './components/ProjectTable';
import {useDashboardData} from './hooks/useDashboardData';
import {ProjectWithStats, CollabRequest, UnreadUpdate, FileData} from './types/dashboard.types';
import {formatTimeAgo, formatDuration} from './utils/timeFormatting';
import {useIsMobile} from '@/utils/user_agent';

export const NewDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    recentActivity,
    starredData,
    myProjects,
    sharedProjects,
    starredProjects,
    starredFiles,
    collaborationRequests,
    unreadNotifications,
    refresh
  } = useDashboardData();

  const [myProjectsExpanded, setMyProjectsExpanded] = useState(false);
  const [sharedProjectsExpanded, setSharedProjectsExpanded] = useState(false);

  const visibleMyProjects = useMemo(() => {
    const sorted = [...myProjects].sort((a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime()
    );
    return myProjectsExpanded ? sorted : sorted.slice(0, 5);
  }, [myProjects, myProjectsExpanded]);

  const visibleSharedProjects = useMemo(() => {
    const sorted = [...sharedProjects].sort((a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime()
    );
    return sharedProjectsExpanded ? sorted : sorted.slice(0, 5);
  }, [sharedProjects, sharedProjectsExpanded]);

  const handleProjectClick = (project: ProjectWithStats) => {
    navigateToPathForReason(
      navigate,
      'navigate_to_project_from_new_dashboard',
      `/workspaces/${project.workspace_id}/projects/${project.id}`
    );
  };

  const handleFileClick = (file: FileData | {attributes?: {duration?: string}; id: string; name: string; workspace_id: string; created_at: string; updated_at: string;}) => {
    navigateToPathForReason(
      navigate,
      'navigate_to_file_from_new_dashboard',
      `/workspaces/${file.workspace_id}/files/${file.id}`
    );
  };

  const handleViewAllWorkspaces = () => {
    navigateToPathForReason(
      navigate,
      'navigate_to_all_workspaces_from_new_dashboard',
      '/workspaces'
    );
  };

  // Group activities by Project and Day
  const formatProjectRevisionActivity = () => {
    if (!recentActivity?.projectRevisions || recentActivity?.projectRevisions.length === 0) {
      return [];
    }

    // First, group revisions by project and day
    const revisionsByProjectAndDay = new Map<string, Map<string, typeof recentActivity.projectRevisions>>();

    // Sort all revisions by date (newest first)
    const sortedRevisions = [...recentActivity.projectRevisions]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    // Group by project and day
    sortedRevisions.forEach(revision => {
      const projectId = revision.project_id;
      const dateStr = new Date(revision.created_at).toDateString(); // Get just the day part

      if (!revisionsByProjectAndDay.has(projectId)) {
        revisionsByProjectAndDay.set(projectId, new Map());
      }

      const projectDays = revisionsByProjectAndDay.get(projectId)!;

      if (!projectDays.has(dateStr)) {
        projectDays.set(dateStr, []);
      }

      projectDays.get(dateStr)!.push(revision);
    });

    // Now create activity items for each project-day combination
    const collapsedActivities: Array<{
      id: string;
      text: string;
      time: string;
      projectId: string;
    }> = [];

    revisionsByProjectAndDay.forEach((dayMap, projectId) => {
      dayMap.forEach((revisions, dateStr) => {
        // Get project and user info from the most recent revision
        const latestRevision = revisions[0]; // Already sorted by date
        const project = recentActivity?.projects.find((p) => p.id === projectId);
        const user = recentActivity?.users.find((u) => u.id === latestRevision.created_by);
        const userName = user?.name || 'Unknown';
        const projectName = project?.name || 'Unknown project';

        // Calculate total files added/updated on this day
        const totalFilesCount = revisions.reduce((sum, rev) => sum + (rev.num_new_files || 0), 0);

        // Split into created and updated activities
        const firstRevision = revisions.find(rev => rev.revision_num === 1);
        const updateRevisions = revisions.filter(rev => rev.revision_num !== 1);

        // Process first revision (created) if it exists
        if (firstRevision) {
          // For created, always use project name
          const maxLength = 20;
          const truncatedName = projectName.length > maxLength ?
            projectName.substring(0, maxLength) + '...' : projectName;

          // If project was created on this day, include all files from all revisions
          // since we'll only show the "created" activity
          const totalFiles = revisions.reduce((sum, rev) => sum + (rev.num_new_files || 0), 0);

          collapsedActivities.push({
            id: `${projectId}-${dateStr}-created`, // Create a unique ID for this creation activity
            text: `${userName} created "${truncatedName}" with ${totalFiles} file${totalFiles !== 1 ? 's' : ''}`,
            time: firstRevision.created_at,
            projectId: projectId
          });
        }

        // Process update revisions only if there was no creation on this day
        else if (updateRevisions.length > 0) {
          // For updates, use revision name if available
          const latestUpdateRevision = updateRevisions[0]; // Already sorted by date
          const displayName = updateRevisions.length > 1 ? latestUpdateRevision.revision_name : projectName;

          const maxLength = 20;
          const truncatedName = displayName.length > maxLength ?
            displayName.substring(0, maxLength) + '...' : displayName;

          // Calculate total files in update revisions
          const totalUpdateFiles = updateRevisions.reduce((sum, rev) => sum + (rev.num_new_files || 0), 0);

          collapsedActivities.push({
            id: `${projectId}-${dateStr}-updated`, // Create a unique ID for this update activity
            text: `${userName} updated "${truncatedName}" with ${totalUpdateFiles} file${totalUpdateFiles !== 1 ? 's' : ''}`,
            time: latestUpdateRevision.created_at,
            projectId: projectId
          });
        }
      });
    });

    // Sort by time (newest first) and limit to 10 items
    return collapsedActivities
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 10);
  };

  return (
    <Container size="xl" py={{base: 'xs', sm: 'md'}} px={{base: 'xs', sm: 'md'}}>
      <Stack gap="md">
        {!isMobile && (
          <Group justify="space-between" wrap="nowrap">
            <Title order={2} ta='center'>Dashboard</Title>
            <Button
              variant="subtle"
              size="compact-sm"
              rightSection={<IconChevronRight size={16} />}
              onClick={handleViewAllWorkspaces}
            >
              View All Workspaces
            </Button>
          </Group>
        )}

        <Grid gutter="md">
          {/* Starred Projects/Files */}
          {(starredProjects.length > 0 || starredFiles.length > 0) && (
            <Grid.Col span={12}>
              <DashboardCard
                title="Starred"
                icon={<IconStar size={18} />}
              >
                {starredProjects.length > 0 && (
                  <>
                    <Text size="md" fw={600}>
                      Projects
                    </Text>
                    <ProjectTable
                      projects={starredProjects}
                      onProjectClick={handleProjectClick}
                      projectFiles={recentActivity?.files || []}
                      attachments={recentActivity?.attachments || []}
                      projectRevisions={recentActivity?.projectRevisions || []}
                    />
                  </>
                )}
                {starredFiles.length > 0 && (
                  <Stack gap="xs" mt="md">
                    <Text size="md" fw={600}>
                      Files
                    </Text>
                    <Table striped highlightOnHover withRowBorders>
                      {!isMobile && (
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{width: '80px', padding: '8px'}}></Table.Th>
                            <Table.Th></Table.Th>
                            <Table.Th style={{width: '140px', textAlign: 'right'}}>Created</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                      )}
                      <Table.Tbody>
                        {starredFiles.slice(0, 5).map((file) => {
                          const fileDuration = file.duration;

                          return (
                            <Table.Tr key={file.id} style={{cursor: 'pointer'}} onClick={() => handleFileClick(file)}>
                              <Table.Td style={{width: '80px', padding: isMobile ? '6px' : '8px'}}>
                                <Group gap="xs" wrap="nowrap" align="center">
                                  {isAudioFile(file.name) ? (
                                    <PlayIcon fileInfo={file} size={16} />
                                  ) : (
                                    <IconFile size={16} style={{opacity: 0.7}} />
                                  )}
                                  {fileDuration && (
                                    <Text size="sm" ml="xs">
                                      {formatDuration(parseInt(fileDuration))}
                                    </Text>
                                  )}
                                </Group>
                              </Table.Td>
                              <Table.Td style={{padding: isMobile ? '6px' : '8px'}}>
                                <Text size="sm" style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                  {file.name}
                                </Text>
                              </Table.Td>
                              <Table.Td style={{textAlign: 'right', padding: isMobile ? '6px' : '8px'}}>
                                <Text size="sm" c="dimmed">
                                  {formatTimeAgo(file.created_at, isMobile)}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                )}
              </DashboardCard>
            </Grid.Col>
          )}

          {/* Collab Requests - Only show if there are any */}
          {false && collaborationRequests.length > 0 && ( // TEMPORARILY DISABLED
            <Grid.Col span={{base: 12, md: 6}}>
              <DashboardCard
                title={`Collab Requests (${collaborationRequests.length})`}
                icon={<IconUserPlus size={18} />}
              >
                <Stack gap="xs">
                  {collaborationRequests.map((request: CollabRequest) => (
                    <Group key={request.id} justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{request.projectName}</Text>
                        <Text size="xs" c="dimmed">From {request.from} • {request.message}</Text>
                      </div>
                      <Group gap="xs">
                        <Button size="xs" variant="light">Accept</Button>
                        <Button size="xs" variant="subtle">Decline</Button>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </DashboardCard>
            </Grid.Col>
          )}

          {/* Unreads - Only show if there are any */}
          {false && unreadNotifications.length > 0 && ( // TEMPORARILY DISABLED
            <Grid.Col span={{base: 12, md: 6}}>
              <DashboardCard
                title={`Unreads (${unreadNotifications.length})`}
                icon={<IconBell size={18} />}
              >
                <Stack gap="xs">
                  {unreadNotifications.slice(0, 5).map((notification: UnreadUpdate) => (
                    <Group key={notification.id} gap="xs">
                      <div style={{flex: 1}}>
                        <Text size="sm">
                          <Text component="span" fw={500}>{notification.from}</Text>{' '}
                          {notification.updateType === 'new_files' && 'uploaded files to'}
                          {notification.updateType === 'mention' && 'mentioned you in'}
                          {notification.updateType === 'comment' && 'commented on'}{' '}
                          <Text component="span" fw={500}>{notification.projectName}</Text>
                        </Text>
                        <Text size="xs" c="dimmed">{notification.preview}</Text>
                      </div>
                    </Group>
                  ))}
                </Stack>
              </DashboardCard>
            </Grid.Col>
          )}

          {/* My Projects */}
          <Grid.Col span={12}>
            <DashboardCard
              title="My Projects"
              icon={<IconFolder size={18} />}
            >
              {myProjects.length === 0 ? (
                <Text size="sm" c="dimmed">No projects yet</Text>
              ) : (
                <ProjectTable
                  projects={visibleMyProjects}
                  onProjectClick={handleProjectClick}
                  showLoadMore={myProjects.length > 5 && !myProjectsExpanded}
                  onLoadMore={() => setMyProjectsExpanded(true)}
                  projectFiles={recentActivity?.files || []}
                  attachments={recentActivity?.attachments || []}
                  projectRevisions={recentActivity?.projectRevisions || []}
                />
              )}
            </DashboardCard>
          </Grid.Col>

          {/* Shared With Me */}
          <Grid.Col span={12}>
            <DashboardCard
              title="Shared With Me"
              icon={<IconFolderShare size={18} />}
            >
              {sharedProjects.length === 0 ? (
                <Text size="sm" c="dimmed">No shared projects</Text>
              ) : (
                <ProjectTable
                  projects={visibleSharedProjects}
                  onProjectClick={handleProjectClick}
                  showLoadMore={sharedProjects.length > 5 && !sharedProjectsExpanded}
                  onLoadMore={() => setSharedProjectsExpanded(true)}
                  projectFiles={recentActivity?.files || []}
                  attachments={recentActivity?.attachments || []}
                  projectRevisions={recentActivity?.projectRevisions || []}
                />
              )}
            </DashboardCard>
          </Grid.Col>

          {/* Recent Project Updates */}
          <Grid.Col span={12}>
            <DashboardCard
              title="Recent Project Updates"
              icon={<IconActivity size={18} />}
            >
              {formatProjectRevisionActivity().length === 0 ? (
                <Text size="sm" c="dimmed">No recent activity</Text>
              ) : (
                <Table striped highlightOnHover withRowBorders>
                  {!isMobile && (
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{width: '80px'}}></Table.Th>
                        <Table.Th>Activity</Table.Th>
                        <Table.Th style={{width: '100px', textAlign: 'right'}}>Time</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                  )}
                  <Table.Tbody>
                    {formatProjectRevisionActivity().map((activity) => {
                      const timeAgo = formatTimeAgo(activity.time, isMobile);
                      const project = recentActivity?.projects.find((p) => p.id === activity.projectId);

                      // Find the bounce file for this project
                      const bounceFile = project?.attributes?.bounce_file ?
                        recentActivity?.files?.find(f =>
                          f.relative_path === project.attributes.bounce_file ||
                          f.name === project.attributes.bounce_file
                        ) : null;

                      const bounceDuration = bounceFile ? bounceFile.attributes?.duration : null;

                      return (
                        <Table.Tr key={activity.id} style={{cursor: 'pointer'}} onClick={() => {
                          if (project) handleProjectClick(project as ProjectWithStats);
                        }}>
                          <Table.Td style={{width: '80px', padding: isMobile ? '6px' : '8px'}}>
                            <Group gap="xs" wrap="nowrap" align="center">
                              {bounceFile && isAudioFile(bounceFile.name) ? (
                                <PlayIcon fileInfo={bounceFile} size={16} />
                              ) : (
                                <IconMusic size={16} style={{opacity: 0.7}} />
                              )}
                              {bounceDuration && (
                                <Text size="sm" ml="xs">
                                  {formatDuration(parseInt(bounceDuration))}
                                </Text>
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td style={{padding: isMobile ? '6px' : '8px'}}>
                            <Text size="sm">{activity.text}</Text>
                          </Table.Td>
                          <Table.Td style={{textAlign: 'right', padding: isMobile ? '6px' : '8px'}}>
                            <Text size="xs" c="dimmed">{timeAgo}</Text>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </DashboardCard>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
};
